import { formatQuantity } from '../../utils/inventoryLabels';
import { matchCodes, sameSlotCode, unitCode } from '../operator/operatorCodes';
import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { taskKeyOf } from '../operator/operatorTaskScope';

/*
 * Picking by route: one walk through the warehouse for one order or a batch,
 * stop by stop. The server plans the route — stops in walking order, lots
 * FEFO — and splits every take among the orders; these are the rules the
 * screen reads it with. A take is in units, always under the unit's own code.
 */

export const MAX_ROUTE_ORDERS = 10;

/** Where the stop on screen is: its slot to scan, its items to take, or the route over. */
export const ROUTE_PHASE = {
  SLOT: 'slot',
  ITEM: 'item',
  DONE: 'done',
};

const ROUTE_ID_PREFIX = 'route:';

/** The order ids of `?orders=812,815`: whole and positive, each once, in the order given. */
export const parseRouteOrders = (param) => {
  const ids = String(param ?? '')
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);
  return [...new Set(ids)];
};

/** Why a route cannot be asked for, or `null`. */
export const routeOrdersProblem = (orderIds) => {
  if (orderIds.length === 0) return 'Elige al menos un pedido para la ruta.';
  if (orderIds.length > MAX_ROUTE_ORDERS) return `Una ruta lleva hasta ${MAX_ROUTE_ORDERS} pedidos.`;
  return null;
};

/** The route's handle, whatever order its orders came in: `route:812,815`. It is the task the screen enters. */
export const routeIdOf = (orderIds) => `${ROUTE_ID_PREFIX}${[...orderIds].sort((a, b) => a - b).join(',')}`;

export const routePath = (orderIds) => `/picking/route?orders=${orderIds.join(',')}`;

/** Whether the screen on is a route: a camera read held there goes to the route, not to the walk. */
export const isRouteTask = (task) =>
  Boolean(task?.key?.startsWith(taskKeyOf(OPERATOR_FLOW.PICKING, ROUTE_ID_PREFIX)));

const plural = (count, one, many) => (count === 1 ? one : many);

export const ordersText = (count) => `${count} ${plural(count, 'pedido', 'pedidos')}`;

/** An order as the floor names it: «ORD-1». Takes a route's order or an item's part. */
export const orderRefOf = (order) => order.reference_id || `Orden #${order.id ?? order.order_id}`;

/** How a band names the route once it is off screen. */
export const routeNameOf = (route, orderIds = []) => {
  const refs = (route?.orders || []).map(orderRefOf);
  if (refs.length > 0 && refs.length <= 2) return `Ruta ${refs.join(' · ')}`;
  return `Ruta de ${ordersText(refs.length || orderIds.length)}`;
};

export const itemPending = (item) => Math.max(Number(item?.quantity || 0) - Number(item?.picked || 0), 0);

export const stopPending = (stop) => (stop?.items || []).reduce((total, item) => total + itemPending(item), 0);

export const isStopDone = (stop) => stopPending(stop) === 0;

/** Whether nothing is left to take anywhere on the route. */
export const isRouteFinished = (route) => Boolean(route) && (route.stops || []).every(isStopDone);

export const findStop = (route, slotCode) =>
  (route?.stops || []).find((stop) => sameSlotCode(stop.slot_code, slotCode)) || null;

export const findItem = (route, key) =>
  (route?.stops || []).flatMap((stop) => stop.items || []).find((item) => item.key === key) || null;

/** The unit a code counts at a stop, `<item key>:<factor>`, for the scan gate; `null` when it is none. */
export const stopUnitOf = (stop, code) => {
  const unit = (stop?.items || [])
    .map((item) => ({ item, match: matchCodes(item.codes, code) }))
    .find(({ match }) => match);
  return unit ? `${unit.item.key}:${unit.match.factor}` : null;
};

/** The stops in walking order: the server's, with the skipped ones last, in the order they were skipped. */
export const walkOrder = (stops, skipped = []) => {
  const list = stops || [];
  const isSkipped = (stop) => skipped.some((code) => sameSlotCode(code, stop.slot_code));
  const later = skipped.map((code) => list.find((stop) => sameSlotCode(stop.slot_code, code))).filter(Boolean);
  return [...list.filter((stop) => !isSkipped(stop)), ...later];
};

/** The stop to go to: the first in walking order with something left; `null` once none has. */
export const currentStopOf = (route, skipped = []) =>
  walkOrder(route?.stops, skipped).find((stop) => !isStopDone(stop)) || null;

/** How many stops are done: the one on screen is the next. */
export const doneStopsOf = (route) => (route?.stops || []).filter(isStopDone).length;

/** Stops with something left: skipping is only a move while there is another. */
export const openStopsOf = (route) => (route?.stops || []).filter((stop) => !isStopDone(stop)).length;

export const stopProgressText = (index, route) =>
  `Parada ${Math.min(index + 1, (route?.stops || []).length)} de ${(route?.stops || []).length}`;

/**
 * After a write at `slotCode`: `{done, next}` once that stop has nothing left
 * — `next` being where the walk goes, `null` at its end —, `null` while it
 * still has. A stop the server took out of the route counts as done.
 */
export const stopHintOf = (route, skipped, slotCode) => {
  const stop = findStop(route, slotCode);
  if (stop && !isStopDone(stop)) return null;
  return { done: stop?.slot_code ?? slotCode, next: currentStopOf(route, skipped)?.slot_code ?? null };
};

/** «A-01 lista · sigue B-03». */
export const stopHintText = (hint) =>
  hint.next ? `${hint.done} lista · sigue ${hint.next}` : `${hint.done} lista · ruta terminada`;

const partPending = (part) => Math.max(Number(part.quantity || 0) - Number(part.picked || 0), 0);

/**
 * How `units` of an item go among its orders, as the server lists them —
 * the oldest first —: `[{order_id, reference_id, quantity}]`.
 */
export const splitByOrder = (item, units) => {
  let left = Number(units) || 0;
  return (item?.by_order || []).reduce((parts, part) => {
    const quantity = Math.min(partPending(part), left);
    left -= quantity;
    return quantity > 0 ? [...parts, { order_id: part.order_id, reference_id: part.reference_id, quantity }] : parts;
  }, []);
};

/** «Para 2 pedidos: ORD-1 (2) · ORD-2 (1)», «Para ORD-1»; nothing on a route of one order. */
export const splitText = (parts, ordersCount) => {
  if (ordersCount <= 1 || parts.length === 0) return '';
  if (parts.length === 1) return `Para ${orderRefOf(parts[0])}`;
  return `Para ${ordersText(parts.length)}: ${parts
    .map((part) => `${orderRefOf(part)} (${formatQuantity(part.quantity)})`)
    .join(' · ')}`;
};

/** The item a take goes to: the one counted last while it lacks something, or the first that does. */
export const focusItemOf = (stop, key) => {
  const open = (stop?.items || []).filter((item) => itemPending(item) > 0);
  return open.find((item) => item.key === key) || open[0] || null;
};

/** What «Tomar» takes of an item: what was counted, never what was not. */
export const takeQuantityOf = (item, counted) => Math.min(Math.max(counted, 0), itemPending(item));

/** «Tomar las 4 de POL-1» when it takes all the item lacks; «Tomar 2 de POL-1» otherwise. */
export const takeLabel = (item, quantity) => {
  const all = quantity > 1 && quantity === itemPending(item);
  return `Tomar ${all ? 'las ' : ''}${formatQuantity(quantity)} de ${item.sku}`;
};

/** The code a take goes under: the unit's, so its quantity is in units whatever was scanned. */
export const unitCodeOf = (item) => unitCode(item?.codes, item?.sku);

const productOn = (stops, code) =>
  stops
    .flatMap((stop) => (stop.items || []).map((item) => ({ stop, item })))
    .find(({ item }) => matchCodes(item.codes, code)) || null;

/**
 * What a code read on the route is, against the stop on screen, without
 * asking the server: the codes the route brings are enough. The stop's slot
 * verifies it; once verified, a product of the stop counts its units — a
 * pack, its factor — up to what the item lacks. Anything else is said.
 *
 * @param {{route: object, stop: object, verified: boolean, localCounts: Record<string, number>}} visit
 * @param {string} code
 * @returns {{kind: 'slot', slotCode: string, again: boolean}
 *   | {kind: 'item', key: string, sku: string, units: number, factor: number}
 *   | {error: string}}
 */
export const readRouteCode = ({ route, stop, verified, localCounts }, code) => {
  const here = stop.slot_code;
  const stops = route.stops || [];
  const otherStop = stops.find((other) => !sameSlotCode(other.slot_code, here) && sameSlotCode(other.slot_code, code));

  if (sameSlotCode(here, code)) return { kind: 'slot', slotCode: here, again: verified };

  if (!verified) {
    const product = productOn(stops, code);
    if (product) return { error: `${product.item.sku} es un producto: primero escanea la ubicación ${here}.` };
    if (otherStop) return { error: `${otherStop.slot_code} es otra parada: ahora toca ${here}.` };
    return { error: `${code} no es ${here}. Escanea la ubicación ${here}.` };
  }

  const matches = (stop.items || [])
    .map((item) => ({ item, match: matchCodes(item.codes, code) }))
    .filter((entry) => entry.match);
  if (matches.length > 0) {
    const room = (item) => itemPending(item) - (localCounts[item.key] || 0);
    const open = matches.find(({ item, match }) => room(item) >= match.factor);
    if (open) {
      return { kind: 'item', key: open.item.key, sku: open.item.sku, units: open.match.factor, factor: open.match.factor };
    }
    const partly = matches.find(({ item }) => room(item) > 0);
    if (partly) {
      return {
        error: `Una caja de ${partly.item.sku} trae ${partly.match.factor} un y faltan ${formatQuantity(room(partly.item))}: escanea la unidad.`,
      };
    }
    const [{ item }] = matches;
    return {
      error:
        itemPending(item) > 0
          ? `Ya escaneaste todo lo que falta de ${item.sku}: tómalo.`
          : `${item.sku} ya está tomado en ${here}.`,
    };
  }

  const elsewhere = productOn(stops.filter((other) => !sameSlotCode(other.slot_code, here)), code);
  if (elsewhere) return { error: `${elsewhere.item.sku} va en ${elsewhere.stop.slot_code}, no en ${here}.` };
  if (otherStop) return { error: `${otherStop.slot_code} es otra parada: termina ${here} o sáltala.` };
  return { error: `${code} no es un producto de ${here}.` };
};

/** Why a shortage cannot be registered yet, written for its button; or `null`. */
export const shortProblem = ({ found, reason }, pending) => {
  const amount = Number(found);
  if (found === '' || Number.isNaN(amount) || amount < 0) return 'Indica cuántas encontraste';
  if (!Number.isInteger(amount)) return 'Se cuenta en unidades enteras';
  if (amount >= pending) return 'Si están todas, tómalas';
  if (!reason) return 'Elige el motivo';
  return null;
};

/** A lot sent, even "", is the one taken; one left out lets the server take the only one. */
const withLot = (lot) => (lot != null ? { lot } : {});

// Every write names all the route's orders: the server shares a take among those that need the item,
// and answers the route of exactly these. A part of them would bring back a route without the rest.
const routeOf = (visit) => ({
  routeId: visit.routeId,
  routeName: routeNameOf(visit.route, visit.orderIds),
  orderIds: visit.orderIds,
});

/**
 * What «Tomar» sends for the stop on screen, as a write's argument; `null`
 * while the stop is not verified, has nothing left, or nothing of its item
 * was counted: the count goes from 0, and what was not counted is not taken.
 */
export const takeArgOf = (visit) => {
  if (visit.stop.phase !== ROUTE_PHASE.ITEM) return null;
  const stop = findStop(visit.route, visit.stop.slotCode);
  const item = focusItemOf(stop, visit.stop.itemKey);
  if (!item) return null;
  const quantity = takeQuantityOf(item, visit.localCounts[item.key] || 0);
  if (quantity <= 0) return null;
  return {
    ...routeOf(visit),
    slotCode: stop.slot_code,
    itemKey: item.key,
    sku: item.sku,
    productCode: unitCodeOf(item),
    quantity,
    ...withLot(item.lot),
  };
};

/** What a shortage of an item of the stop on screen sends; `null` if the item is not there. */
export const shortArgOf = (visit, itemKey, { found, reason, notes }) => {
  const stop = findStop(visit.route, visit.stop.slotCode);
  const item = (stop?.items || []).find((candidate) => candidate.key === itemKey);
  if (!item) return null;
  const note = String(notes ?? '').trim();
  return {
    ...routeOf(visit),
    slotCode: stop.slot_code,
    itemKey,
    sku: item.sku,
    productId: item.product_id,
    ...withLot(item.lot),
    found: Number(found),
    pending: itemPending(item),
    reason,
    ...(note ? { notes: note } : {}),
  };
};

/** @param {{slotCode: string, productCode: string, quantity: number, lot?: string, orderIds: number[]}} arg */
export const buildTakeBody = ({ slotCode, productCode, quantity, lot, orderIds }, key) => ({
  slot_code: slotCode,
  product_code: productCode,
  quantity: Number(quantity),
  ...withLot(lot),
  order_ids: orderIds,
  idempotency_key: key,
});

export const buildShortBody = ({ slotCode, productId, lot, found, reason, notes, orderIds }, key) => ({
  slot_code: slotCode,
  product_id: productId,
  ...withLot(lot),
  found_quantity: Number(found),
  reason,
  ...(notes ? { notes } : {}),
  order_ids: orderIds,
  idempotency_key: key,
});

/** `order_ids` are the route's: without them the server answers only the orders of that take. */
export const buildUndoBody = ({ pickKey, orderIds }, key) => ({
  pick_key: pickKey,
  ...(orderIds?.length ? { order_ids: orderIds } : {}),
  idempotency_key: key,
});

/** How the orders of a take's answer got it: «ORD-1 (2) · ORD-2 (1)»; nothing on a route of one order. */
const appliedText = (applied, orders = []) => {
  if (orders.length <= 1) return '';
  const byOrder = new Map();
  applied.forEach((part) => byOrder.set(part.order_id, (byOrder.get(part.order_id) || 0) + Number(part.quantity || 0)));
  return [...byOrder.entries()]
    .map(([orderId, quantity]) => {
      const order = orders.find((candidate) => candidate.id === orderId) || { id: orderId };
      return `${orderRefOf(order)} (${formatQuantity(quantity)})`;
    })
    .join(' · ');
};

const hintAfter = (hint) => (hint ? ` ${stopHintText(hint)}.` : '');

/** What to say once a take went through; `hint` is where the walk goes, while the route is on screen. */
export const takeMessage = (payload, arg, hint) => {
  if (payload.replayed) return { tone: 'success', message: `Esa toma ya estaba registrada.${hintAfter(hint)}` };
  const split = appliedText(payload.applied || [], payload.route?.orders);
  return {
    tone: 'success',
    message: `${formatQuantity(arg.quantity)} un de ${arg.sku}${split ? `: ${split}` : ''}.${hintAfter(hint)}`,
  };
};

export const shortMessage = (payload, arg, hint) => {
  if (payload.replayed) return { tone: 'success', message: `Ese faltante ya estaba registrado.${hintAfter(hint)}` };
  return {
    tone: 'warning',
    message: `Faltante de ${arg.sku} en ${arg.slotCode}: encontraste ${formatQuantity(arg.found)} de ${formatQuantity(arg.pending)}.${hintAfter(hint)}`,
  };
};

export const undoMessage = (payload, arg) =>
  payload.replayed
    ? { tone: 'success', message: 'Esa toma ya estaba deshecha.' }
    : { tone: 'success', message: `Deshecho: devuelve ${formatQuantity(arg.units)} un de ${arg.sku} a ${arg.slotCode}.` };

export const orderDone = (order) => Number(order.picked || 0) >= Number(order.units || 0);

/** The route's units, over its orders: what the bar fills with. */
export const routeUnitsOf = (route) =>
  (route?.orders || []).reduce(
    (total, order) => ({ units: total.units + Number(order.units || 0), picked: total.picked + Number(order.picked || 0) }),
    { units: 0, picked: 0 }
  );

/** «3 de 4 pedidos pickeados». */
export const routeResultTitle = (route) => {
  const orders = route?.orders || [];
  if (orders.length === 0) return 'Nada que pickear';
  const done = orders.filter(orderDone).length;
  return `${done} de ${ordersText(orders.length)} ${plural(orders.length, 'pickeado', 'pickeados')}`;
};
